/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURL } from '@/config/serviceAPI';
import { API_URLS } from '@/constants/apiUrls';
import { createQuery } from '@/utils/createReactQuery';
import { getAxios } from '@/lib/axios';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { useCountry } from '@/hooks/useLocale';
import { useIsBot } from '@/utils/useIsBot';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useProductSkuDetails } from '@/features/product/api/getProductSkuDetails';
import { useHeaderData } from '@/features/header/api/getHeader';
import { type AxiosInstance } from 'axios';
import { type CertonaPricing } from '@/types';
import { type ProductModelACES } from '@/api/types/products-types';
import type {
  AffinityRelatedProductsOptionsACES,
  AffinityRelatedProductsResponseACES,
  AffinityRelatedProductsData,
  AffinityRelatedProductACES,
} from '../interface';

const URL = `${requestBaseURL}/ecomm/b2c/v1/browse/v3/affinity/related-parts`;

export const getAffinityRelatedProductsResultsACES = async (
  options: AffinityRelatedProductsOptionsACES,
  axiosInstance?: AxiosInstance
) => {
  let certonaPricing = null;
  const {
    data: { affinityRelatedProducts },
  } = await getAxios(axiosInstance).get<AffinityRelatedProductsResponseACES>(URL, {
    withCredentials: false,
    params: options,
  });
  const affinityData = affinityRelatedProducts.slice(
    0,
    options?.recordsPerPage || affinityRelatedProducts.length
  );

  if (affinityData.length) {
    const skuNumbers = affinityData.map((part) => part.skuId).join(',');
    const { data: certonaPricingForAffinityItems } = await getAxios(axiosInstance).get<
      CertonaPricing[]
    >(`${API_URLS.CERTONA_PRICING}${skuNumbers}?storeNumber=${options.storeId ?? ''}`);

    certonaPricing = certonaPricingForAffinityItems;
  }

  return selector(affinityData, certonaPricing);
};

const selector = (
  affinityData: AffinityRelatedProductACES[],
  certonaPricing: CertonaPricing[] | null
): AffinityRelatedProductsData => {
  if (certonaPricing) {
    const relatedProductsWithPricing = affinityData.map((product) => {
      const pricingForProduct = certonaPricing.find((part) => part.skuId === product.skuId);
      const {
        productDetailsPageUrl: seoUrl,
        partGroupId: productId,
        ...affinityRelatedProduct
      } = product;

      if (!pricingForProduct) {
        // eslint-disable-next-line no-console
        console.error(`Missing Certona pricing for product SKU: ${product.skuId}`);
        return null;
      }

      return {
        ...affinityRelatedProduct,
        ...pricingForProduct,
        productId,
        seoUrl,
      };
    });

    return relatedProductsWithPricing.filter(Boolean) as AffinityRelatedProductsData;
  }

  return null;
};

const { useData: useAffinityData } = createQuery<
  AffinityRelatedProductsData,
  AffinityRelatedProductsOptionsACES
>('affinityRelatedParts', async (options, axiosInstance) =>
  getAffinityRelatedProductsResultsACES({ ...options }, axiosInstance)
);

export const useAffinityRelatedProductsACES = ({
  enabled = true,
  itemsToDisplay,
}: { enabled?: boolean; itemsToDisplay?: number } = {}) => {
  const country = useCountry();
  const isBot = useIsBot();
  const { data: productDetailsData, isSuccess: isProductDetailsSuccess } = useProductDetails();
  const product = productDetailsData?.product as ProductModelACES | undefined;
  const { itemId, partGroupId = '', vehicleFit, recordType } = product ?? {};
  const { vehicleMap, storeNumber: storeId } = useHeaderData()?.data || {};
  const { year, makeId, modelId, vehicleTypeId } = vehicleMap || {};
  const isVehicleSelected = !!vehicleTypeId;
  const vehicleQuestions = extractVehicleQuestions(vehicleMap?.vehicleQuestionsIds, true) ?? '';
  const { data: partSkuDetailsData } = useProductSkuDetails({
    skuIds: isProductDetailsSuccess && itemId ? [itemId] : null,
  });
  const isPartAvailable =
    partSkuDetailsData?.[0]?.fulfillmentOptions?.some(({ available }) => available) ?? false;
  const partFitsSelectedVehicle =
    recordType?.toLowerCase() === 'app' ? isVehicleSelected && vehicleFit !== 'DOES_NOT_FIT' : true;
  const queryEnabled = enabled && !isBot && isPartAvailable && partFitsSelectedVehicle;

  const optionsACES: AffinityRelatedProductsOptionsACES = {
    // required
    partGroupId,
    country,
    // optional
    year: year ? String(year) : undefined,
    makeId: makeId ? String(makeId) : undefined,
    modelId: modelId ? String(modelId) : undefined,
    storeId,
    vehicleTypeId: vehicleTypeId ? String(vehicleTypeId) : undefined,
    vehicleQuestions: (vehicleQuestions as string) || undefined,
    recordsPerPage: itemsToDisplay,
  };

  return useAffinityData({
    ...optionsACES,
    enabled: Boolean(queryEnabled),
  });
};
